import React from "react";
import { useTranslation, Trans } from "react-i18next";
import { Circle, Search, House, ArrowRight } from "lucide-react";

// NOTE: also relies on .select-service__title / .select-service__divider / .select-service__back-btn,
// defined in SelectService.css. Not imported here — only available because
// the parent (SelectService.jsx) imports that file first.
import "./IsPropertyFound.css";

function FeatureRow({ title, description }) { 
    return (
        <div className="feature-row">
            <div className="feature-row__dot-wrapper">
                <Circle className="feature-row__dot" />
            </div>
            <div className="feature-row__text">
                <span className="feature-row__title">{title}</span>
                <span className="feature-row__description">{description}</span>
            </div>
        </div>
    );
}

function PropertyCard({ 
    statusDot,
    statusLabel,
    icon,
    heading,
    description,
    features,
    primaryLabel,
    secondaryLabel,
    footnote,
    onSimulate,
    onRegister,
    t,
}) {
    return (
        <div className="property-card">

            <div className="property-card__top-bar">
                <div className="property-card__pill">
                    <Circle className={`property-card__dot property-card__dot--${statusDot}`} />
                    <span className="property-card__status-label">{statusLabel}</span>
                </div>
                <div className="property-card__icon">{icon}</div>
            </div>

            <h2 className="property-card__heading">{heading}</h2>
            <p className="property-card__description">{description}</p>

            <div className="property-card__features">
                {features.map((f, i) => (
                <FeatureRow key={i} title={f.title} description={f.description} />
                ))}
            </div>

            <div className="property-card__buttons">
                <button type="button" className="property-card__btn--simulate" onClick={onSimulate}>
                    {primaryLabel} 
                    <ArrowRight size={16} strokeWidth={2} />
                </button>
                <button type="button" className="property-card__btn--register" onClick={onRegister}>
                    {secondaryLabel}
                </button>
                <p className="property-card__footnote">{footnote}</p>
            </div>

        </div>
    );
}

export default function IsPropertyFound({ onContinue, onBack }) {
    const { t } = useTranslation("select-service", {
    keyPrefix: "isPropertyFound"
    });

    return (
        <div>
            <h1 className="select-service__title">{t("title")}</h1>
            <hr className="select-service__divider" />
      
            <div className="is-property-found__content">
                <div className="is-property-found__cards">

                    <PropertyCard
                        statusDot="green"
                        statusLabel={t('notFound.label')}
                        icon={<Search className="property-card__icon-svg" />}
                        heading={t('notFound.heading')}
                        description={
                            <>
                                <strong className="property-card__description-bold">
                                    {t('notFound.descBold')}
                                </strong>
                                <span className="property-card__description-body">
                                    <Trans
                                        i18nKey="isPropertyFound.notFound.desc"
                                        ns="select-service"
                                        components={{ b: <strong /> }}
                                    />
                                </span>
                            </>
                        }
                        features={[
                            {
                                title: t('notFound.feature1Title'),
                                description: t('notFound.feature1Desc'),
                            },
                            {
                                title: t('notFound.feature2Title'),
                                description: t('notFound.feature2Desc'),
                            },
                        ]}
                        primaryLabel={t('notFound.primaryBtn')}
                        secondaryLabel={t('notFound.secondaryBtn')}
                        footnote={t('notFound.footnote')}
                        onSimulate={() => onContinue("simulate")}
                        onRegister={() => onContinue("register")}
                        t={t}
                    />

                    <PropertyCard
                        statusDot="gray"
                        statusLabel={t('found.label')}
                        icon={<House className="property-card__icon-svg" />}
                        heading={t('found.heading')}
                        description={
                            <>
                                <strong className="property-card__description-bold">
                                    {t('found.descBold')}
                                </strong>
                                <span className="property-card__description-body">
                                    <Trans
                                        i18nKey="isPropertyFound.found.desc"
                                        ns="select-service"
                                        components={{ b: <strong /> }}
                                    />
                                </span>
                            </>
                        }
                        features={[
                            {
                                title: t('found.feature1Title'),
                                description: t('found.feature1Desc'),
                            },
                            {
                                title: t('found.feature2Title'),
                                description: t('found.feature2Desc'),
                            },
                        ]}
                        primaryLabel={t('found.primaryBtn')}
                        secondaryLabel={t('found.secondaryBtn')}
                        footnote={t('found.footnote')}
                        onSimulate={() => onContinue("simulate")}
                        onRegister={() => onContinue("register")}
                        t={t}
                    />

                </div>
        
                <div className="is-property-found__back-row">
                    <button type="button" className="select-service__back-btn" onClick={onBack}>
                        {t("select-service:back", { keyPrefix: "" })}
                    </button>
                </div>
            </div>
        </div>
    );
}